/**
 * The one form's state, and what a save of it sends (FR-47).
 *
 * The same shape on every screen that edits a role: the review step after an
 * upload, a new role typed by hand, the role's own edit, and a manager's
 * change request. Pure, so what each screen sends can be checked offline.
 */
import type {
  BulkRow, ChangeValue, DocSection, JobDescription, ProposableField, ReviewDraft, RoleSave,
} from '../types'
import {
  STARTING_HEADINGS, blankSection, fromEditable, rowSections, sameSections, sectionsOf, titleLead, toEditable,
} from './sections'
import type { EditableSection } from './sections'

/** The form: the key details, then the sections. */
export interface RoleFormState {
  title: string
  family: string
  level: string
  industry: string
  location: string
  payMin: string
  payMax: string
  /** `year` or `hour`. Empty reads as a year, like a role saved before this existed. */
  payUnit: string
  sections: EditableSection[]
  /**
   * Sections at the top that only repeat the document's title, with nothing
   * under them (FR-68). Kept off the screen and put back, unchanged, in front
   * of the sections on every save, so the form never changes what is stored.
   */
  kept?: DocSection[]
}

/**
 * The sections a form opens with: the title at the top kept aside, the rest
 * to edit.
 */
function opened(sections: DocSection[], title: string): Pick<RoleFormState, 'sections' | 'kept'> {
  const n = titleLead(sections, title)
  return n ? { sections: toEditable(sections.slice(n)), kept: sections.slice(0, n) } : { sections: toEditable(sections) }
}

/** The form's sections as the role stores them, the kept title back in front. */
const saved = (form: RoleFormState): DocSection[] => [...(form.kept ?? []), ...fromEditable(form.sections)]

/**
 * What an upload read, carried unseen into the save (FR-49).
 *
 * The hidden fields exactly as the reader wrote them, and the sections they
 * were read from. Sent back untouched, so a role saved from an upload nobody
 * edited is the role it always was.
 */
export interface ReadFields {
  fields: Omit<RoleSave, 'sections' | 'fieldsReadFrom' | keyof KeyDetails>
  /** The document's sections, where the section read worked. */
  sections?: DocSection[]
}

type KeyDetails = Omit<RoleFormState, 'sections' | 'kept'>

const keyDetails = (f: RoleFormState): KeyDetails => ({
  title: f.title, family: f.family, level: f.level, industry: f.industry,
  location: f.location, payMin: f.payMin, payMax: f.payMax, payUnit: f.payUnit,
})

/** A new role typed by hand: no key details yet, and four empty sections (FR-53). */
export const blankForm = (family: string): RoleFormState => ({
  title: '', family, level: 'Mid', industry: '', location: '',
  payMin: '', payMax: '', payUnit: 'year',
  sections: STARTING_HEADINGS.map((h) => blankSection(h)),
})

/** A saved role, in the form: its own sections, or ones built from its fields (FR-52). */
export const formFromRole = (j: JobDescription): RoleFormState => ({
  title: j.title,
  family: j.family,
  level: j.level,
  industry: j.industry,
  location: j.location,
  payMin: String(j.payMin),
  payMax: String(j.payMax),
  // Absent reads as a year, which is what a role saved before this means.
  payUnit: j.payUnit ?? 'year',
  ...opened(sectionsOf(j), j.title),
})

/**
 * A mass upload row, in the form (FR-58): its key details, then its sections,
 * or for a row without them the sections built from its columns (FR-61).
 *
 * A row carries no pay unit, because the spreadsheet's columns are fixed, so
 * it reads as a year like every role saved before the unit existed.
 */
export const formFromRow = (r: BulkRow): RoleFormState => ({
  title: r.title,
  family: r.family,
  level: r.level || 'Mid',
  industry: r.industry ?? '',
  location: r.location,
  payMin: r.payMin ? String(r.payMin) : '',
  payMax: r.payMax ? String(r.payMax) : '',
  payUnit: 'year',
  // Every section, the title line included: this panel shows the document
  // as mass upload read it (TC-190), unlike the role screen (FR-68).
  sections: toEditable(rowSections(r)),
})

/**
 * What saving the Edit panel on a mass upload row sends (FR-59).
 *
 * The key details always, as before, which is what marks a worked out family,
 * level or industry as decided by the person (FR-30a). The sections only when
 * they say something different from what the panel opened with, so a row
 * saved for a key detail alone imports exactly as it would have.
 */
export function rowEditBody(r: BulkRow, form: RoleFormState): {
  set: Record<string, string>
  sections?: DocSection[]
} {
  const set = {
    title: form.title, family: form.family, level: form.level, industry: form.industry,
    location: form.location, payMin: form.payMin, payMax: form.payMax,
  }
  const sections = saved(form)
  return sameSections(sections, rowSections(r)) ? { set } : { set, sections }
}

/** The hidden fields an upload reads, by name. Everything else in a draft is a key detail. */
const READ_FIELDS = [
  'summary', 'responsibilities', 'skills', 'quals',
  'physicalDemands', 'workEnvironment', 'totalRewards', 'compensationNote', 'legal',
] as const

/**
 * What an upload read, in the form, with what it read kept aside.
 *
 * The form shows the document's sections. If the section read failed, it
 * shows sections built from the fields instead, so the person still sees the
 * text and can save it (FR-46, FR-52).
 */
export function formFromUpload(draft: ReviewDraft, fallbackFamily: string): {
  form: RoleFormState
  read: ReadFields
} {
  const fields: ReadFields['fields'] = {}
  for (const name of READ_FIELDS) {
    if (draft[name] !== undefined) fields[name] = draft[name]
  }
  const shown = draft.sections?.length
    ? draft.sections
    : sectionsOf({
      summary: draft.summary,
      responsibilities: (draft.responsibilities ?? '').split('\n'),
      skills: (draft.skills ?? '').split(','),
      quals: (draft.quals ?? '').split('\n'),
      physicalDemands: draft.physicalDemands,
      workEnvironment: draft.workEnvironment,
      totalRewards: draft.totalRewards,
      compensationNote: draft.compensationNote,
      legal: draft.legal,
    })
  return {
    form: {
      title: draft.title ?? '',
      family: draft.family || fallbackFamily,
      // Kept as the reader left it, so an unread level is picked by a person
      // rather than defaulted, as it always was.
      level: draft.level ?? 'Mid',
      industry: draft.industry ?? '',
      location: draft.location ?? '',
      payMin: draft.payMin ?? '',
      payMax: draft.payMax ?? '',
      payUnit: draft.payUnit || 'year',
      ...opened(shown, draft.title ?? ''),
    },
    read: { fields, sections: draft.sections?.length ? draft.sections : undefined },
  }
}

/**
 * What saving a new role sends.
 *
 * From an upload: the key details, the sections, and what the upload read
 * together with the sections it read it from. The server compares the two
 * and reads the fields again only if the person changed the text. Where the
 * document's sections could not be read and the person left the built ones
 * alone, no sections are sent and the role stays as it was read.
 *
 * Typed by hand: the key details and the sections, and the server reads the
 * hidden fields from them (FR-53).
 */
export function createBody(form: RoleFormState, read?: ReadFields): RoleSave {
  const sections = saved(form)
  if (!read) return { ...keyDetails(form), sections }
  if (!read.sections) {
    const built = toEditable(sectionsOf(hiddenAsRole(read.fields)))
    if (sameSections(sections, fromEditable(built))) return { ...keyDetails(form), ...read.fields }
    return { ...keyDetails(form), ...read.fields, sections }
  }
  return { ...keyDetails(form), ...read.fields, sections, fieldsReadFrom: read.sections }
}

/** What an edit sends: the key details and the sections, and no hidden field (FR-50). */
export const editBody = (form: RoleFormState): RoleSave => ({
  ...keyDetails(form),
  sections: saved(form),
})

const hiddenAsRole = (f: ReadFields['fields']) => ({
  summary: f.summary,
  responsibilities: (f.responsibilities ?? '').split('\n'),
  skills: (f.skills ?? '').split(','),
  quals: (f.quals ?? '').split('\n'),
  physicalDemands: f.physicalDemands,
  workEnvironment: f.workEnvironment,
  totalRewards: f.totalRewards,
  compensationNote: f.compensationNote,
  legal: f.legal,
})

/**
 * Why the form cannot be saved yet, or nothing.
 *
 * Title, family and level, as the server requires (FR-1a), and at least one
 * line under some heading, because a role with no text cannot be
 * benchmarked (FR-53).
 */
export function formProblem(form: RoleFormState): string | null {
  if (!form.title.trim()) return 'Give the role a title before saving.'
  if (!form.family.trim()) return 'Give the role a job family before saving.'
  if (!fromEditable(form.sections).some((s) => s.lines.length > 0)) {
    return 'Write at least one line under a heading before saving.'
  }
  return null
}

/** Only what a manager changed, of the fields of FR-17a. The sections count as one. */
export function changedFields(jd: JobDescription, form: RoleFormState): Partial<Record<ProposableField, ChangeValue>> {
  const out: Partial<Record<ProposableField, ChangeValue>> = {}
  const num = (v: string, fallback: number) => {
    const n = parseFloat(v)
    return isNaN(n) ? fallback : n
  }
  if (form.title.trim() !== jd.title) out.title = form.title.trim()
  if (form.level !== jd.level) out.level = form.level
  if (form.location.trim() !== jd.location) out.location = form.location.trim()
  if (num(form.payMin, jd.payMin) !== jd.payMin) out.payMin = num(form.payMin, jd.payMin)
  if (num(form.payMax, jd.payMax) !== jd.payMax) out.payMax = num(form.payMax, jd.payMax)
  const sections = saved(form)
  if (!sameSections(sections, sectionsOf(jd))) out.sections = sections
  return out
}
